import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, MapPin, Mail, Phone, Globe, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Section, Button } from "@/components/UI";

export default function PractitionerProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/practitioners/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true)); }, [id]);

  if (notFound) return (
    <Section className="pt-40 pb-32 text-center">
      <p className="text-ccpm-taupe">This practitioner profile could not be found.</p>
      <Link to="/registry" className="text-ccpm-olive mt-4 inline-block" data-testid="profile-notfound-back">← Back to the registry</Link>
    </Section>
  );
  if (!p) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-ccpm-olive" /></div>;

  return (
    <>
      <div className="pt-32 pb-6 border-b border-ccpm-border bg-ccpm-stone/40">
        <Section>
          <Link to="/registry" className="inline-flex items-center gap-2 text-sm text-ccpm-olive hover:underline" data-testid="profile-back"><ArrowLeft size={16} /> Practitioner Registry</Link>
        </Section>
      </div>

      <Section className="py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-5">
            <div className="relative overflow-hidden aspect-[4/5] bg-ccpm-stone arch-top">
              {p.photo_url
                ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" data-testid="profile-photo" />
                : <div className="w-full h-full flex items-center justify-center text-ccpm-olive-soft font-serif text-6xl">{p.name.charAt(0)}</div>}
            </div>
          </motion.div>

          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
              <span className="inline-flex items-center gap-2 overline text-ccpm-olive"><ShieldCheck size={16} strokeWidth={1.5} /> CCPM Verified Practitioner</span>
              <h1 className="font-serif text-4xl lg:text-6xl text-ccpm-charcoal tracking-tight mt-4 leading-[1.02]" data-testid="profile-name">{p.name}</h1>
              {p.credentials && <p className="mt-3 text-ccpm-olive font-semibold">{p.credentials}</p>}
              {p.clinic_name && <p className="mt-1 text-lg text-ccpm-charcoal">{p.clinic_name}</p>}
              <p className="mt-2 inline-flex items-center gap-2 text-ccpm-taupe"><MapPin size={16} strokeWidth={1.5} />{p.city}, {p.province}</p>

              <div className="mt-8">
                <span className="overline text-ccpm-taupe block mb-3">Restorative services</span>
                <div className="flex flex-wrap gap-2" data-testid="profile-services">
                  {p.services.map((s) => <span key={s} className="text-sm px-3 py-1.5 bg-ccpm-stone text-ccpm-taupe border border-ccpm-border">{s}</span>)}
                </div>
              </div>

              {p.bio && (
                <div className="mt-8">
                  <span className="overline text-ccpm-taupe block mb-3">About</span>
                  <p className="text-ccpm-charcoal/90 font-light leading-relaxed text-[1.05rem] max-w-xl">{p.bio}</p>
                </div>
              )}

              <div className="mt-10 border-t border-ccpm-border pt-8 grid sm:grid-cols-2 gap-5">
                {p.email && <a href={`mailto:${p.email}`} className="group flex items-center gap-3 text-ccpm-charcoal hover:text-ccpm-olive" data-testid="profile-email"><Mail size={18} strokeWidth={1.5} className="text-ccpm-olive" /><span className="text-sm">{p.email}</span></a>}
                {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-3 text-ccpm-charcoal hover:text-ccpm-olive" data-testid="profile-phone"><Phone size={18} strokeWidth={1.5} className="text-ccpm-olive" /><span className="text-sm">{p.phone}</span></a>}
                {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-ccpm-charcoal hover:text-ccpm-olive" data-testid="profile-website"><Globe size={18} strokeWidth={1.5} className="text-ccpm-olive" /><span className="text-sm">Visit website</span></a>}
              </div>

              <div className="mt-10 p-6 bg-ccpm-stone border border-ccpm-border">
                <p className="text-sm text-ccpm-taupe font-light leading-relaxed">This practitioner's certification has been verified against the CCPM national standards of practice. To report a concern, please <Link to="/contact" className="text-ccpm-olive underline">contact the Council</Link>.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>
    </>
  );
}
